/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, type FunctionComponent } from 'react'

import { EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import BadgeList from '../../../../../components/BadgeList'
import { FIXED_RECIPIENTS } from '../../../../constants'
import { getAlertThresholds } from '../../../../BudgetsTable/utils'

import Section from './Section'

import type { Budget } from '../../../../types'

type Props = {
  alerts: Array<Pick<Budget['alerts'][number], 'threshold' | 'threshold_type'>>
  isHidden: boolean
}

const Alerts: FunctionComponent<Props> = ({ alerts, isHidden }) => {
  if (isHidden) {
    return (
      <EuiText size='s' color='subdued'>
        <FormattedMessage
          id='billing.notifications.budgets.budget-settings.alerts.hidden'
          defaultMessage='Configure notification thresholds and recipients.'
        />
      </EuiText>
    )
  }

  return (
    <Fragment>
      <EuiText size='s' color='subdued'>
        <FormattedMessage
          id='billing.notifications.budgets.alerts.description'
          defaultMessage='The following settings are pre-set and cannot be modified at this time.'
        />
      </EuiText>

      <EuiSpacer size='l' />

      <Section
        title={
          <FormattedMessage
            id='billing.notifications.budgets.alerts.settings-details.thresholds.title'
            defaultMessage='Notify at'
          />
        }
        helpText={
          <FormattedMessage
            id='billing.notifications.budgets.alerts.settings-details.thresholds.help'
            defaultMessage='Budget thresholds will trigger an email notification.'
          />
        }
      >
        <BadgeList items={getAlertThresholds(alerts)} />
      </Section>

      <EuiSpacer size='l' />
      <Section
        title={
          <FormattedMessage
            id='billing.notifications.budgets.alerts.settings-details.recipients.title'
            defaultMessage='Recipients'
          />
        }
        helpText={
          <FormattedMessage
            id='billing.notifications.budgets.alerts.settings-details.recipients.help'
            defaultMessage='Budget alerts will be sent to these roles in your organization.'
          />
        }
      >
        <BadgeList items={FIXED_RECIPIENTS} />
      </Section>
    </Fragment>
  )
}

export default Alerts
